import { useCallback, useEffect, useRef, useState } from 'react'

export function useAsyncResource (
  fetcher,
  deps = [],
  { initialData = null, enabled = true } = {}
) {
  const [data, setData] = useState(initialData)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState(null)

  const fetcherRef = useRef(fetcher)
  const requestIdRef = useRef(0)

  fetcherRef.current = fetcher

  const reload = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false)
      return undefined
    }

    const requestId = ++requestIdRef.current

    setIsLoading(true)
    setError(null)

    try {
      const result = await fetcherRef.current()

      if (requestId === requestIdRef.current) {
        setData(result)
      }

      return result
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setError(requestError?.message || 'Request failed.')
      }

      return undefined
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [enabled, ...deps])

  useEffect(() => {
    reload()
    return () => {
      requestIdRef.current += 1
    }
  }, [reload])

  return {
    data,
    isLoading,
    error,
    reload,
    setData
  }
}
